import React, { useEffect, useState } from "react";
import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  Image,
  ActivityIndicator
} from "react-native";
import Glob from "src/globalConstants";
import Database from "src/backend/database";
import Analytics from "src/backend/analytics";
import NavBar from "src/components/navBar";
import { MaterialIcons } from "@expo/vector-icons";
import SearchBar from "src/components/searchBar";
import moment from "moment";
import Button from "src/components/Button";
import Dropdown from "src/components/Dropdown";
import { Skeleton } from "native-base";
import Rex from "src/globalState";
import Util from "src/utility";
import NavBarButton from "src/components/NavBarButton";

const { width } = Glob.get("dimensions");
const DEFAULT_INVOICES_TO_SHOW = 3;
const DEFAULT_SUBSCRIPTIONS_TO_SHOW = 3;
const LOADING = "LOADING";

const DashboardCard = ({ title, children, icon }) => (
  <View style={styles.card}>
    <View style={styles.cardHeader}>
      {icon}
      <Text style={styles.cardTitle}>{title}</Text>
    </View>
    {children}
  </View>
);

const InvoiceSkeletonLoader = () => (
  <View style={{ width: "100%" }}>
    {/* Summary amount skeleton */}
    <View style={[styles.skeletonSummaryContainer, { paddingVertical: 25 }]}>
      <Skeleton h="8" w="40%" rounded="md" startColor="#eee" endColor="#ddd" />
      <Skeleton
        h="4"
        w="30%"
        rounded="full"
        mt="2"
        startColor="#eee"
        endColor="#ddd"
      />
    </View>

    {/* Search bar skeleton */}
    <View style={styles.skeletonSearchBarContainer}>
      <Skeleton h="10" rounded="md" startColor="#eee" endColor="#ddd" />
    </View>

    {/* Invoice items */}
    {[1, 2, 3].map((item) => (
      <View key={item} style={styles.skeletonInvoiceItem}>
        <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
          <View>
            <Skeleton
              h="4"
              w="120"
              rounded="full"
              mb="2"
              startColor="#eee"
              endColor="#ddd"
            />
            <Skeleton
              h="3"
              w="150"
              rounded="full"
              startColor="#eee"
              endColor="#ddd"
            />
          </View>
          <Skeleton
            h="6"
            w="70"
            rounded="md"
            startColor="#eee"
            endColor="#ddd"
          />
        </View>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            marginTop: 12
          }}
        >
          <Skeleton
            h="3"
            w="100"
            rounded="full"
            startColor="#eee"
            endColor="#ddd"
          />
          <Skeleton
            h="3"
            w="10"
            rounded="full"
            startColor="#eee"
            endColor="#ddd"
          />
        </View>
      </View>
    ))}
  </View>
);

const PaymentHistorySkeletonLoader = () => (
  <View style={{ width: "100%" }}>
    {[1, 2, 3].map((item) => (
      <View key={item} style={styles.skeletonInvoiceItem}>
        <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
          <View>
            <Skeleton
              h="4"
              w={100 + item * 20}
              rounded="full"
              mb="2"
              startColor="#eee"
              endColor="#ddd"
            />
            <Skeleton
              h="3"
              w={130 + item * 10}
              rounded="full"
              startColor="#eee"
              endColor="#ddd"
            />
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <Skeleton
              h="6"
              w="70"
              rounded="md"
              startColor="#eee"
              endColor="#ddd"
            />
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginTop: 4
              }}
            >
              <Skeleton
                h="2"
                w="2"
                rounded="full"
                startColor="#2DD881"
                endColor="#2DD881"
                mr="1"
              />
              <Skeleton
                h="3"
                w="30"
                rounded="full"
                startColor="#eee"
                endColor="#ddd"
              />
            </View>
          </View>
        </View>
      </View>
    ))}

    {/* View All Payments button skeleton */}
    <View
      style={[styles.historyButton, { marginTop: 15, marginHorizontal: 4 }]}
    >
      <Skeleton
        h="4"
        w="60%"
        rounded="full"
        startColor="#eee"
        endColor="#ddd"
      />
    </View>
  </View>
);

const SubscriptionSkeletonLoader = () => (
  <View style={{ width: "100%" }}>
    {[1, 2, 3].map((item) => (
      <View key={item} style={styles.skeletonInvoiceItem}>
        <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
          <View>
            <Skeleton
              h="4"
              w={100 + item * 20}
              rounded="full"
              mb="2"
              startColor="#eee"
              endColor="#ddd"
            />
            <Skeleton
              h="3"
              w={130 + item * 10}
              rounded="full"
              startColor="#eee"
              endColor="#ddd"
            />
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <Skeleton
              h="6"
              w="70"
              rounded="md"
              startColor="#eee"
              endColor="#ddd"
            />
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginTop: 4
              }}
            >
              <Skeleton
                h="2"
                w="2"
                rounded="full"
                startColor="#2DD881"
                endColor="#2DD881"
                mr="1"
              />
              <Skeleton
                h="3"
                w="30"
                rounded="full"
                startColor="#eee"
                endColor="#ddd"
              />
            </View>
          </View>
        </View>
      </View>
    ))}

    {/* View All button skeleton */}
    <View
      style={[styles.historyButton, { marginTop: 15, marginHorizontal: 4 }]}
    >
      <Skeleton
        h="4"
        w="60%"
        rounded="full"
        startColor="#eee"
        endColor="#ddd"
      />
    </View>
  </View>
);

const SubscriptionItem = ({ subscription, onPress, children }) => {
  // Get dynamic colors
  const buttonColor = Rex.getConfig()?.colors?.button || "#805AD5";
  const lightButtonColor = Util.getSuperLightColor(buttonColor);
  const mediumButtonColor = `${buttonColor}80`; // Using 50% opacity

  // Check for child data
  const tcChildID = subscription?.metadata?.tcChildID;
  const child = tcChildID ? children[tcChildID] : null;
  const hasPhoto = child?.profile_photo && child.profile_photo.includes("http");

  // Check if we have a tcChildID but child data isn't loaded yet
  const isLoadingChild =
    tcChildID && !child && Object.keys(children).length > 0;

  // Format status text and color
  let statusColor = "#3182CE";
  let statusText = "Active";

  if (subscription.status === "canceled") {
    statusColor = "#718096";
    statusText = "Canceled";
  } else if (subscription.status === "past_due") {
    statusColor = "#F6AD55";
    statusText = "Past due";
  } else if (
    subscription.status === "trialing" &&
    subscription.trial_end &&
    subscription.trial_end > moment().unix()
  ) {
    statusText = `Starts ${moment
      .unix(subscription.trial_end)
      .format("MMM D")}`;
  } else if (
    subscription.status === "unpaid" ||
    subscription.status === "incomplete_expired"
  ) {
    statusColor = "#FC8181";
    statusText = "Unpaid";
  }

  // Format subscription data - handle nested customer object
  const customer = subscription.customer || {};
  const customerName = customer.name || "Customer";
  const customerEmail = customer.email || "";

  // Get product info from first item
  const items = subscription.items?.data || [];
  const firstItem = items[0] || {};
  const price = firstItem.price || {};
  const product = price.product || {};

  const productName =
    typeof product === "string"
      ? "Subscription"
      : product.name || "Subscription";

  const amount = price.unit_amount || 0;
  const interval = price.recurring?.interval || "month";
  const intervalCount = price.recurring?.interval_count || 1;

  // Format price and interval
  const formattedPrice = `$${(amount / 100).toFixed(2).replace(".00", "")}`;
  const formattedInterval =
    intervalCount === 1 ? interval : `${intervalCount} ${interval}s`;

  // Check if subscription has discounts
  const hasDiscounts =
    subscription.discounts && subscription.discounts.length > 0;

  // Handle different collection methods
  const isInvoice = subscription.collection_method === "send_invoice";
  if (isInvoice && subscription.status === "active") {
    statusColor = "#3182CE"; // Blue for invoice sent
    statusText = "Invoice sent";
  }

  return (
    <TouchableOpacity style={styles.invoiceItem} onPress={onPress}>
      <View style={styles.invoiceHeader}>
        <View>
          <Text style={styles.customerName}>{customerName}</Text>
          <Text style={styles.customerEmail}>{customerEmail}</Text>

          {/* Loading child UI */}
          {isLoadingChild && (
            <View
              style={[
                styles.childContainer,
                { backgroundColor: lightButtonColor }
              ]}
            >
              <Skeleton
                size="5"
                rounded="full"
                startColor={lightButtonColor}
                endColor={mediumButtonColor}
                style={{ marginRight: 6, width: 20, height: 20 }}
              />
              <Skeleton
                height="3"
                width="100"
                rounded="full"
                startColor={lightButtonColor}
                endColor={mediumButtonColor}
              />
            </View>
          )}

          {/* Child UI if child exists */}
          {child && (
            <View
              style={[
                styles.childContainer,
                { backgroundColor: lightButtonColor }
              ]}
            >
              {hasPhoto ? (
                <Image
                  source={{ uri: child.profile_photo }}
                  style={styles.childPhoto}
                />
              ) : (
                <View
                  style={[styles.childAvatar, { backgroundColor: buttonColor }]}
                >
                  <Text style={styles.avatarText}>
                    {child.first_name.charAt(0)}
                    {child.last_name.charAt(0)}
                  </Text>
                </View>
              )}
              <Text style={[styles.childName, { color: buttonColor }]}>
                {child.first_name} {child.last_name}
              </Text>
            </View>
          )}

          <Text style={styles.productNameText}>{productName}</Text>
        </View>
        <View style={styles.amountContainer}>
          {hasDiscounts && (
            <Text
              style={[
                styles.discountBadge,
                { color: buttonColor, backgroundColor: lightButtonColor }
              ]}
            >
              Discounted
            </Text>
          )}
          <Text style={[styles.amount, { fontSize: 16 }]}>
            {formattedPrice}/{formattedInterval}
          </Text>
        </View>
      </View>
      <View style={styles.invoiceFooter}>
        <Text style={styles.invoiceDate}>
          Started on {moment(subscription.created * 1000).format("MMM D, YYYY")}
        </Text>
        <View style={styles.statusContainer}>
          <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
          <Text style={styles.statusText}>{statusText}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const PaymentItem = ({ item, onPress, children, isCharge = false }) => {
  // Different property locations depending on if it's a charge or invoice
  const invoice = isCharge ? item.invoice : item;
  const tcChildID = isCharge
    ? item.metadata?.tcChildID ||
      (invoice && typeof invoice === "object" && invoice.metadata?.tcChildID)
    : item?.metadata?.tcChildID;

  const child = tcChildID ? children[tcChildID] : null;
  const hasPhoto = child?.profile_photo && child.profile_photo.includes("http");

  // Get dynamic colors
  const buttonColor = Rex.getConfig()?.colors?.button || "#805AD5";
  const lightButtonColor = Util.getSuperLightColor(buttonColor);
  const mediumButtonColor = `${buttonColor}80`; // Using 50% opacity

  // Check if we have a tcChildID but child data isn't loaded yet
  const isLoadingChild =
    tcChildID && !child && Object.keys(children).length > 0;

  // Determine content based on whether this is a charge or invoice
  let customerName;
  let customerEmail;
  let amount;
  let date;
  let statusColor;
  let statusText;
  let description;
  let hasDiscounts = false;

  if (isCharge) {
    // It's a charge
    customerName = item.billing_details?.name || "Customer";
    customerEmail = item.billing_details?.email || "";
    amount = (item.amount / 100).toFixed(2).replace(".00", "");
    date = moment.unix(item.created).format("MMM D, YYYY");
    statusColor = "#2DD881"; // Green for paid
    statusText = "Paid";

    // Get description from charge or its invoice
    description = item.description || "Payment";
    if (item.invoice && typeof item.invoice === "object") {
      const invoiceLines = item.invoice.lines?.data;
      if (invoiceLines && invoiceLines.length > 0) {
        const firstItem = Util.getOrderedInvoiceItems(invoiceLines)[0];
        if (firstItem?.description) {
          description = firstItem.description;
        }
      }

      // Check if invoice has discounts
      hasDiscounts =
        item.invoice.discounts && item.invoice.discounts.length > 0;
    }
  } else {
    // It's an invoice
    customerName = item.customer_name;
    customerEmail = item.customer_email;
    amount = (item.amount_due / 100).toFixed(2).replace(".00", "");

    // Determine date to display
    date = moment(
      (item.paid && item.status_transitions && item.status_transitions.paid_at
        ? item.status_transitions.paid_at
        : item.created) * 1000
    ).format("MMM D, YYYY");

    // Determine status
    if (item.paid) {
      statusColor = "#2DD881"; // Green for paid
      statusText = "Paid";
    } else if (item.collection_method === "send_invoice") {
      statusColor = "#3182CE"; // Blue for invoice sent
      statusText = "Invoice sent";
    } else {
      statusColor = "#F6AD55"; // Default: orange for unpaid
      statusText = "Awaiting payment";
    }

    // Check if invoice has discounts
    hasDiscounts = item.discounts && item.discounts.length > 0;
  }

  return (
    <TouchableOpacity style={styles.invoiceItem} onPress={onPress}>
      <View style={styles.invoiceHeader}>
        <View style={{ maxWidth: "75%" }}>
          <Text style={styles.customerName}>{customerName}</Text>
          <Text style={styles.customerEmail}>{customerEmail}</Text>
          {isLoadingChild && (
            <View
              style={[
                styles.childContainer,
                { backgroundColor: lightButtonColor }
              ]}
            >
              <Skeleton
                size="5"
                rounded="full"
                startColor={lightButtonColor}
                endColor={mediumButtonColor}
                style={{ marginRight: 6, width: 20, height: 20 }}
              />
              <Skeleton
                height="3"
                width="100"
                rounded="full"
                startColor={lightButtonColor}
                endColor={mediumButtonColor}
              />
            </View>
          )}
          {child && (
            <View
              style={[
                styles.childContainer,
                { backgroundColor: lightButtonColor }
              ]}
            >
              {hasPhoto ? (
                <Image
                  source={{ uri: child.profile_photo }}
                  style={styles.childPhoto}
                />
              ) : (
                <View
                  style={[styles.childAvatar, { backgroundColor: buttonColor }]}
                >
                  <Text style={styles.avatarText}>
                    {child.first_name.charAt(0)}
                    {child.last_name.charAt(0)}
                  </Text>
                </View>
              )}
              <Text style={[styles.childName, { color: buttonColor }]}>
                {child.first_name} {child.last_name}
              </Text>
            </View>
          )}
          {isCharge && (
            <Text style={styles.productNameText}>{description}</Text>
          )}
        </View>
        <View style={styles.amountContainer}>
          {hasDiscounts && (
            <Text
              style={[
                styles.discountBadge,
                { color: buttonColor, backgroundColor: lightButtonColor }
              ]}
            >
              Discounted
            </Text>
          )}
          <Text
            style={[
              styles.amount,
              isCharge || item.paid ? styles.paidAmount : styles.unpaidAmount
            ]}
          >
            ${amount}
          </Text>
        </View>
      </View>
      <View style={styles.invoiceFooter}>
        <Text style={styles.invoiceDate}>
          {isCharge || item.paid ? "Paid on " : "Created on "} {date}
        </Text>
        <View style={styles.statusContainer}>
          <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
          <Text style={styles.statusText}>{statusText}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

export default function CommerceBilling({ navigation }) {
  const [isLoadingUnpaidInvoices, setIsLoadingUnpaidInvoices] = useState(true);
  const [isLoadingPayments, setIsLoadingPayments] = useState(true);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(true);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredUnpaidInvoices, setFilteredUnpaidInvoices] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentSearchQuery, setPaymentSearchQuery] = useState("");
  const [subscriptionSearchQuery, setSubscriptionSearchQuery] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [totalUnpaid, setTotalUnpaid] = useState(LOADING);
  const [totalEarnings, setTotalEarnings] = useState(LOADING);
  const [isShowingAllUnpaidInvoices, setIsShowingAllUnpaidInvoices] = useState(
    false
  );
  const [isShowingAllPayments, setIsShowingAllPayments] = useState(false);
  const [isShowingAllSubscriptions, setIsShowingAllSubscriptions] = useState(
    false
  );
  const [children, setChildren] = useState([]);
  const [paymentCharges, setPaymentCharges] = useState([]);
  const [filteredPaymentCharges, setFilteredPaymentCharges] = useState([]);
  const [isLoadingExport, setIsLoadingExport] = useState(false);

  useEffect(() => {
    Analytics.logEvent("view_commerceBilling");
    fetchUnpaidInvoices();
    fetchPaymentHistory();
    fetchSubscriptions();
    fetchUsers();
    fetchAnalytics();
    if (Rex.getConfig()?.transparentClassroomIntegrationEnabled) {
      fetchChildren();
    }
  }, []);

  const fetchChildren = async () => {
    const response = await Database.tcCheckIntegrationStatus();
    const { status } = response || {};
    const connected = status === Glob.get("tcIntegrationStatuses").CONNECTED;
    if (connected) {
      const childrenResponse = await Database.tcFetchChildren();
      const childrenByID = {};
      (childrenResponse?.data || []).forEach((child) => {
        childrenByID[child.id] = child;
      });
      setChildren(childrenByID);
    }
  };

  const fetchAnalytics = async () => {
    const analyticsResponse = await Database.stripeConnectedAccountFetchInvoiceAnalytics();
    const chargesAnalyticsResponse = await Database.stripeConnectedAccountFetchChargesAnalytics();
    if (analyticsResponse.success) {
      const { totalUnpaid: unpaid } = analyticsResponse;
      setTotalUnpaid(unpaid);
    }
    if (chargesAnalyticsResponse.success) {
      const { totalEarnings: earnings } = chargesAnalyticsResponse;
      setTotalEarnings(earnings);
    }
  };

  const fetchUnpaidInvoices = async (fetchAll = false) => {
    setIsLoadingUnpaidInvoices(true);
    try {
      // Fetch unpaid invoices
      const unpaidInvoicesResponse = await Database.stripeConnectedAccountFetchAllInvoices(
        { unpaidOnly: true, limit: fetchAll ? 100 : DEFAULT_INVOICES_TO_SHOW }
      );

      const unpaidInvoicesData = unpaidInvoicesResponse.invoices || [];
      setUnpaidInvoices(unpaidInvoicesData);
      setFilteredUnpaidInvoices(unpaidInvoicesData);
      setIsShowingAllUnpaidInvoices(fetchAll);
    } catch (error) {
      console.error("Error fetching unpaid invoices:", error);
    } finally {
      setIsLoadingUnpaidInvoices(false);
    }
  };

  const fetchPaymentHistory = async (fetchAll = false) => {
    setIsLoadingPayments(true);
    try {
      const paymentsHistory = await Database.stripeConnectedAccountFetchPaymentHistory(
        {
          limit: fetchAll ? 100 : DEFAULT_INVOICES_TO_SHOW,
          fetchAll
        }
      );

      if (paymentsHistory.success) {
        const charges = paymentsHistory.allCharges || [];
        setPaymentCharges(charges);
        setFilteredPaymentCharges(charges);
        setIsShowingAllPayments(fetchAll);
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);
    } finally {
      setIsLoadingPayments(false);
    }
  };

  // TODO: Implement pagination here (and elsewhere)
  const fetchSubscriptions = async (fetchAll = false) => {
    setIsLoadingSubscriptions(true);
    try {
      const subscriptionsResponse = await Database.stripeConnectedAccountFetchSubscriptions(
        {
          statuses: ["active", "trialing"],
          ...(!fetchAll ? { limit: DEFAULT_SUBSCRIPTIONS_TO_SHOW } : {})
        }
      );
      const subscriptionsData = subscriptionsResponse.subscriptions || [];
      setSubscriptions(subscriptionsData);
      setFilteredSubscriptions(subscriptionsData);
      setIsShowingAllSubscriptions(fetchAll);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    } finally {
      setIsLoadingSubscriptions(false);
    }
  };

  const loadAllSubscriptions = () => {
    Analytics.logEvent("touch_commerceBilling_viewAllSubscriptions");
    setIsShowingAllSubscriptions(true);
    fetchSubscriptions(true);
  };

  const loadAllPayments = () => {
    Analytics.logEvent("touch_commerceBilling_viewAllPaymentHistory");
    fetchPaymentHistory(true);
  };

  const loadAllUnpaidInvoices = () => {
    Analytics.logEvent("touch_commerceBilling_viewAllUnpaidInvoices");
    fetchUnpaidInvoices(true);
  };

  const fetchUsers = async () => {
    try {
      const allUsers = await Database.fetchAllUsers();
      const userItems = allUsers.map((user) => ({
        value: user.uid,
        text: `${user.firstName} ${user.lastName} (${user.email})`
      }));
      setUsers(userItems);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredUnpaidInvoices(unpaidInvoices);
      return;
    }

    const filtered = unpaidInvoices.filter((invoice) => {
      const searchString = `${invoice.customer_name} ${invoice.customer_email} ${invoice.amount_due}`.toLowerCase();
      return searchString.includes(query.toLowerCase());
    });
    setFilteredUnpaidInvoices(filtered);
  };

  const handlePaymentSearch = (query) => {
    setPaymentSearchQuery(query);
    if (!query.trim()) {
      setFilteredPaymentCharges(paymentCharges);
      return;
    }

    const filtered = paymentCharges.filter((charge) => {
      // Extract data from charge for searching
      const customerName = charge.billing_details?.name || "";
      const customerEmail = charge.billing_details?.email || "";
      const amount = charge.amount || 0;

      // Get description - check invoice for better description
      let description = charge.description || "";
      if (charge.invoice && typeof charge.invoice === "object") {
        const invoiceLines = charge.invoice.lines?.data;
        if (invoiceLines && invoiceLines.length > 0) {
          const firstItem = Util.getOrderedInvoiceItems(invoiceLines)[0];
          if (firstItem?.description) {
            description = firstItem.description;
          }
        }
      }

      const searchString = `${customerName} ${customerEmail} ${description} ${amount}`.toLowerCase();
      return searchString.includes(query.toLowerCase());
    });

    setFilteredPaymentCharges(filtered);
  };

  const handleSubscriptionSearch = (query) => {
    setSubscriptionSearchQuery(query);
    if (!query.trim()) {
      setFilteredSubscriptions(subscriptions);
      return;
    }

    const filtered = subscriptions.filter((subscription) => {
      const customer = subscription.customer_name || "";
      const email = subscription.customer_email || "";
      const product = subscription.items?.data?.[0]?.price?.product?.name || "";
      const searchString = `${customer} ${email} ${product}`.toLowerCase();
      return searchString.includes(query.toLowerCase());
    });
    setFilteredSubscriptions(filtered);
  };

  const handleUserSelect = async (userID) => {
    setSelectedUser(userID);
    setShowUserDropdown(false);
    handleCreateInvoice(userID);
  };

  const handleCreateInvoice = (userID) => {
    navigation.push("commerceCreateInvoice", {
      userID,
      onInvoiceCreated: () => {
        fetchUnpaidInvoices(isShowingAllUnpaidInvoices);
        fetchPaymentHistory(isShowingAllPayments);
      }
    });
  };

  const handleInvoicePress = (invoice) => {
    navigation.push("commerceInvoiceDetails", {
      invoice,
      onInvoiceUpdated: () => {
        fetchUnpaidInvoices(isShowingAllUnpaidInvoices);
      }
    });
  };

  const handlePaymentPress = (payment) => {
    if (payment.invoice && typeof payment.invoice === "object") {
      navigation.push("commerceInvoiceDetails", {
        invoice: payment.invoice,
        onInvoiceUpdated: () => {
          fetchUnpaidInvoices(isShowingAllUnpaidInvoices);
          fetchPaymentHistory(isShowingAllPayments);
        }
      });
    } else {
      navigation.push("commerceChargeDetails", { charge: payment });
    }
  };

  const handleSubscriptionPress = (subscription) => {
    navigation.push("commerceSubscriptionDetails", {
      subscription,
      onSubscriptionUpdated: () => {
        fetchSubscriptions(isShowingAllSubscriptions);
      }
    });
  };

  const exportPaymentHistoryAsCSV = async () => {
    Analytics.logEvent("touch_commerceBilling_exportPaymentHistory");

    // Only run the export logic on web
    if (Platform.OS === "web") {
      // Set loading state
      setIsLoadingExport(true);

      try {
        // First, load all payments if they haven't been loaded already
        if (!isShowingAllPayments) {
          const paymentsHistory = await Database.stripeConnectedAccountFetchPaymentHistory(
            {
              limit: 100,
              fetchAll: true
            }
          );

          if (paymentsHistory.success) {
            // Use these charges for the export instead of the current state
            generateAndDownloadCSV(paymentsHistory.allCharges || []);
          } else {
            console.error("Failed to fetch all payments for export");
          }
        } else {
          // All payments are already loaded, just use the current state
          generateAndDownloadCSV(paymentCharges);
        }
      } catch (error) {
        console.error("Error exporting payment history:", error);
      } finally {
        setIsLoadingExport(false);
      }
    }
  };

  const generateAndDownloadCSV = (charges) => {
    if (Platform.OS !== "web") return;

    // Import FileSaver only on web platform
    let FileSaver;
    if (Platform.OS === "web") {
      // This is dynamic to handle web-only modules
      FileSaver = require("file-saver");
    }

    // Define columns for CSV
    const columns = [
      "Date",
      "Customer",
      "Email",
      "Description",
      "Amount",
      "Discounts",
      "Status",
      "Invoice Number",
      "Child",
      "Line Items"
    ];

    const csvRows = charges.map((charge) => {
      // Extract description from the charge
      let description = charge.description || "Payment";
      let lineItems = "";

      // Check if this charge has expanded invoice data with line items
      if (charge.invoice && typeof charge.invoice === "object") {
        const invoiceLines = charge.invoice.lines?.data;
        if (invoiceLines && invoiceLines.length > 0) {
          const firstItem = Util.getOrderedInvoiceItems(invoiceLines)[0];
          if (firstItem?.description) {
            const { description: itemDescription } = firstItem;
            description = itemDescription;
          }

          // Format all line items for the CSV
          lineItems = invoiceLines
            .map((item) => {
              const amount = (item.amount / 100).toFixed(2);
              return `${item.description || "Item"} ($${amount})`;
            })
            .join("; ");
        }
      }

      // Format the date from unix timestamp
      const date = moment.unix(charge.created).format("MM/DD/YYYY h:mm A");

      // Format the amount from cents to dollars
      const amount = (charge.amount / 100).toFixed(2);

      // Get customer details
      const customerName = charge.billing_details?.name || "Customer";
      const customerEmail = charge.billing_details?.email || "";

      // Extract discount information if available
      let discountInfo = "";
      if (charge.invoice && typeof charge.invoice === "object") {
        const { invoice } = charge;

        // Check for discounts array
        if (invoice.discounts && invoice.discounts.length > 0) {
          discountInfo = invoice.discounts
            .map((discount) => {
              if (discount.coupon) {
                const couponName = discount.coupon.name
                  ? `${discount.coupon.name}: `
                  : "";
                if (discount.coupon.percent_off) {
                  return `${couponName}${discount.coupon.percent_off}% off`;
                }
                if (discount.coupon.amount_off) {
                  return `${couponName}$${(
                    discount.coupon.amount_off / 100
                  ).toFixed(2)} off`;
                }
              }
              return "Discount applied";
            })
            .join("; ");
        }

        // Check for single discount object (older API versions)
        else if (invoice.discount && invoice.discount.coupon) {
          const { coupon } = invoice.discount;
          const couponName = coupon.name ? `${coupon.name}: ` : "";
          if (coupon.percent_off) {
            discountInfo = `${couponName}${coupon.percent_off}% off`;
          } else if (coupon.amount_off) {
            discountInfo = `${couponName}$${(coupon.amount_off / 100).toFixed(
              2
            )} off`;
          } else {
            discountInfo = couponName
              ? `${couponName}Discount applied`
              : "Discount applied";
          }
        }
      }

      // Use invoice number if available, otherwise use ID
      const invoiceNumber = charge.invoice?.number || charge.invoice?.id || "";

      // Get child information if available
      let childName = "";
      const tcChildID =
        charge.metadata?.tcChildID ||
        (charge.invoice &&
          typeof charge.invoice === "object" &&
          charge.invoice.metadata?.tcChildID);

      if (tcChildID && children[tcChildID]) {
        const child = children[tcChildID];
        if (child) {
          childName = `${child.first_name || ""} ${child.last_name ||
            ""}`.trim();
        }
      }

      return [
        date,
        customerName.replace(/,/g, ""),
        customerEmail.replace(/,/g, ""),
        description.replace(/,/g, ""),
        `$${amount}`,
        discountInfo.replace(/,/g, ""),
        "Paid",
        invoiceNumber,
        childName.replace(/,/g, ""),
        lineItems.replace(/,/g, "")
      ].join(",");
    });

    const csvContent = [columns.join(","), ...csvRows].join("\n");

    // Create and download the file using the FileSaver library
    // This is web-only code
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;"
    });
    FileSaver.saveAs(
      blob,
      `Payment_History_${moment().format("YYYY-MM-DD")}.csv`
    );
  };

  return (
    <>
      <KeyboardAvoidingView
        style={styles.pageContent}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
      >
        <NavBar
          navigation={navigation}
          text="Billing & Invoices"
          RightButton={
            <NavBarButton
              type="help"
              navigation={navigation}
              data={{
                title: "Questions about billing?",
                message:
                  "We know how critical billing is to your school, so if you have any questions at all, please reach out to us immediately for support.",
                isContactUs: true,
                isSchool: true
              }}
            />
          }
        />
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
        >
          <View style={[styles.card, styles.searchContainer]}>
            {showUserDropdown ? (
              <Dropdown
                placeholder="Who do you want to bill?"
                items={users}
                value={selectedUser}
                onSelect={handleUserSelect}
                enableAutocomplete
                startOpen
                onCollapse={() => setShowUserDropdown(false)}
              />
            ) : (
              <Button
                text="Bill Someone"
                onPress={() =>
                  navigation.push("commerceBillSomeone", {
                    onInvoiceCreated: () => {
                      setIsLoadingUnpaidInvoices(true);
                      setIsLoadingSubscriptions(true);
                      setIsLoadingPayments(true);
                      setTimeout(() => {
                        fetchUnpaidInvoices(isShowingAllUnpaidInvoices);
                        fetchSubscriptions(isShowingAllSubscriptions);
                        fetchPaymentHistory(isShowingAllPayments);
                        // Add a small delay to allow Stripe to fully process any new subscriptions
                        // todo: ideally handle this better
                      }, 3000);
                    }
                  })
                }
                style={styles.createButton}
                textStyle={{ fontWeight: "bold" }}
              />
            )}
          </View>

          <DashboardCard
            title="Outstanding Payments"
            icon={<MaterialIcons name="payment" size={24} color="#4A5568" />}
          >
            {isLoadingUnpaidInvoices ? (
              <InvoiceSkeletonLoader />
            ) : (
              <>
                <View style={styles.summaryContainer}>
                  {totalUnpaid === LOADING ? (
                    <Skeleton
                      h="8"
                      w="50%"
                      rounded="md"
                      startColor="#eee"
                      endColor="#ddd"
                      speed={3}
                    />
                  ) : (
                    <Text style={styles.summaryAmount}>
                      $
                      {(totalUnpaid / 100).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </Text>
                  )}
                  <Text style={styles.summaryLabel}>
                    {totalUnpaid === LOADING
                      ? "Calculating Total Outstanding..."
                      : "Total Outstanding"}
                  </Text>
                </View>
                <View style={styles.searchBarContainer}>
                  {isShowingAllUnpaidInvoices && (
                    <SearchBar
                      placeholder="Search unpaid invoices..."
                      onChangeText={handleSearch}
                      value={searchQuery}
                      customStyle={styles.searchBar}
                    />
                  )}
                </View>
                {filteredUnpaidInvoices.map((invoice) => (
                  <PaymentItem
                    key={invoice.id}
                    item={invoice}
                    onPress={() => handleInvoicePress(invoice)}
                  >
                    {children}
                  </PaymentItem>
                ))}

                {!isShowingAllUnpaidInvoices &&
                  filteredUnpaidInvoices.length >= DEFAULT_INVOICES_TO_SHOW && (
                    <TouchableOpacity
                      style={[styles.historyButton, { marginTop: 15 }]}
                      onPress={loadAllUnpaidInvoices}
                      disabled={isLoadingUnpaidInvoices}
                    >
                      <Text style={styles.historyButtonText}>
                        View More Unpaid Invoices
                      </Text>
                      <MaterialIcons
                        name="expand-more"
                        size={20}
                        color="#4A5568"
                      />
                    </TouchableOpacity>
                  )}
              </>
            )}
          </DashboardCard>

          <DashboardCard
            title="Active Subscriptions"
            icon={<MaterialIcons name="sync" size={24} color="#4A5568" />}
          >
            {isLoadingSubscriptions ? (
              <SubscriptionSkeletonLoader />
            ) : (
              <>
                <View style={styles.searchBarContainer}>
                  {isShowingAllSubscriptions && (
                    <SearchBar
                      placeholder="Search subscriptions..."
                      onChangeText={handleSubscriptionSearch}
                      value={subscriptionSearchQuery}
                      customStyle={styles.searchBar}
                    />
                  )}
                </View>
                {subscriptions.length > 0 ? (
                  <>
                    {filteredSubscriptions.map((subscription) => (
                      <SubscriptionItem
                        key={subscription.id}
                        subscription={subscription}
                        onPress={() => handleSubscriptionPress(subscription)}
                      >
                        {children}
                      </SubscriptionItem>
                    ))}

                    {!isShowingAllSubscriptions && (
                      <TouchableOpacity
                        style={[styles.historyButton, { marginTop: 15 }]}
                        onPress={loadAllSubscriptions}
                        disabled={isLoadingSubscriptions}
                      >
                        <Text style={styles.historyButtonText}>
                          View More Subscriptions
                        </Text>
                        <MaterialIcons
                          name="expand-more"
                          size={20}
                          color="#4A5568"
                        />
                      </TouchableOpacity>
                    )}
                  </>
                ) : (
                  <Text style={styles.emptyText}>No active subscriptions</Text>
                )}
              </>
            )}
          </DashboardCard>

          <DashboardCard
            title="Payment History"
            icon={
              <MaterialIcons name="receipt-long" size={24} color="#4A5568" />
            }
          >
            {isLoadingPayments ? (
              <PaymentHistorySkeletonLoader />
            ) : (
              <>
                <View style={styles.summaryContainer}>
                  {totalEarnings === LOADING ? (
                    <Skeleton
                      h="8"
                      w="50%"
                      rounded="md"
                      startColor="#eee"
                      endColor="#ddd"
                      speed={3}
                    />
                  ) : (
                    <Text style={styles.summaryAmount}>
                      $
                      {(totalEarnings / 100).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </Text>
                  )}
                  {/* todo: use totalEarnings from charge analytics */}
                  <Text style={styles.summaryLabel}>
                    {totalEarnings === LOADING
                      ? "Calculating Total Received..."
                      : "Total Received"}
                  </Text>
                </View>
                <View style={styles.searchBarContainer}>
                  {isShowingAllPayments && (
                    <SearchBar
                      placeholder="Search payment history..."
                      onChangeText={handlePaymentSearch}
                      value={paymentSearchQuery}
                      customStyle={styles.searchBar}
                    />
                  )}
                </View>
                {paymentCharges.length > 0 ? (
                  <>
                    {(isShowingAllPayments && paymentSearchQuery.trim()
                      ? filteredPaymentCharges
                      : paymentCharges
                    ).map((charge) => (
                      <PaymentItem
                        key={charge.id}
                        item={charge}
                        isCharge
                        onPress={() => handlePaymentPress(charge)}
                      >
                        {children}
                      </PaymentItem>
                    ))}

                    <View style={styles.paymentHistoryActions}>
                      {!isShowingAllPayments && (
                        <TouchableOpacity
                          style={styles.historyButton}
                          onPress={loadAllPayments}
                          disabled={isLoadingPayments}
                        >
                          <Text style={styles.historyButtonText}>
                            View All Payments
                          </Text>
                          <MaterialIcons
                            name="expand-more"
                            size={20}
                            color="#4A5568"
                          />
                        </TouchableOpacity>
                      )}

                      {/* Only show export button on web platforms */}
                      {Platform.OS === "web" && (
                        <TouchableOpacity
                          style={styles.exportButton}
                          onPress={exportPaymentHistoryAsCSV}
                          disabled={isLoadingPayments || isLoadingExport}
                        >
                          {isLoadingExport ? (
                            <>
                              <ActivityIndicator size="small" color="#4A5568" />
                              <Text style={styles.historyButtonText}>
                                Exporting...
                              </Text>
                            </>
                          ) : (
                            <>
                              <Text style={styles.historyButtonText}>
                                Export as CSV
                              </Text>
                              <MaterialIcons
                                name="file-download"
                                size={20}
                                color="#4A5568"
                              />
                            </>
                          )}
                        </TouchableOpacity>
                      )}
                    </View>
                    {isLoadingExport && (
                      <Text style={styles.exportingNote}>
                        This might take a minute or two.
                      </Text>
                    )}
                  </>
                ) : (
                  <Text style={styles.emptyText}>No payment history</Text>
                )}
              </>
            )}
          </DashboardCard>
        </ScrollView>
      </KeyboardAvoidingView>
    </>
  );
}

const styles = StyleSheet.create({
  pageContent: {
    flex: 1,
    alignItems: "center",
    backgroundColor: "#F5F7FA"
  },
  scrollView: {
    width
  },
  scrollViewContent: {
    padding: 20,
    paddingBottom: 50
  },
  searchContainer: {
    marginBottom: 20,
    width: "100%",
    padding: 16
  },
  createButton: {
    backgroundColor: "#2DD881"
  },
  card: {
    backgroundColor: "white",
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2D3748",
    marginLeft: 12
  },
  summaryContainer: {
    backgroundColor: "#F7FAFC",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    alignItems: "center"
  },
  summaryAmount: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#2D3748"
  },
  summaryLabel: {
    fontSize: 14,
    color: "#718096",
    marginTop: 4
  },
  invoiceItem: {
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 8,
    padding: 16,
    marginBottom: 12
  },
  invoiceHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12
  },
  customerName: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2D3748"
  },
  customerEmail: {
    fontSize: 14,
    color: "#718096"
  },
  amountContainer: {
    alignItems: "flex-end"
  },
  amount: {
    fontSize: 18,
    fontWeight: "600"
  },
  paidAmount: {
    color: "#2DD881"
  },
  unpaidAmount: {
    color: "#F6AD55"
  },
  invoiceFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  },
  invoiceDate: {
    fontSize: 14,
    color: "#718096"
  },
  statusContainer: {
    flexDirection: "row",
    alignItems: "center"
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6
  },
  statusText: {
    fontSize: 14,
    color: "#4A5568"
  },
  searchBarContainer: {
    marginBottom: 16
  },
  searchBar: {
    backgroundColor: "#F7FAFC",
    borderRadius: 8,
    padding: 12
  },
  invoiceNote: {
    fontSize: 13,
    color: "#718096",
    fontStyle: "italic",
    marginTop: 8
  },
  discountBadge: {
    fontSize: 11,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginBottom: 4,
    fontWeight: "500"
  },
  historyButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 12,
    backgroundColor: "#F7FAFC",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    flex: 1
  },
  historyButtonText: {
    fontSize: 16,
    color: "#4A5568",
    fontWeight: "500"
  },
  emptyText: {
    fontSize: 16,
    color: "#718096",
    textAlign: "center",
    paddingVertical: 12
  },
  skeletonSummaryContainer: {
    marginBottom: 16,
    alignItems: "center",
    borderRadius: 8,
    backgroundColor: "#f5f5f5",
    marginHorizontal: 4
  },
  skeletonSearchBarContainer: {
    marginBottom: 16,
    marginHorizontal: 4
  },
  skeletonInvoiceItem: {
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 8,
    padding: 16,
    marginBottom: 12
  },
  childContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    alignSelf: "flex-start"
  },
  childPhoto: {
    width: 20,
    height: 20,
    borderRadius: 10,
    marginRight: 6
  },
  childAvatar: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: Rex.getConfig()?.colors?.button || "#805AD5",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 6
  },
  avatarText: {
    fontSize: 10,
    fontWeight: "bold",
    color: "white"
  },
  childName: {
    fontSize: 13,
    color: Rex.getConfig()?.colors?.button || "#805AD5",
    fontWeight: "500"
  },
  productNameText: {
    fontSize: 13,
    color: "#718096",
    fontWeight: "500",
    marginTop: 4
  },
  exportButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 12,
    backgroundColor: "#F7FAFC",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    flex: 1
  },
  paymentHistoryActions: {
    marginTop: 15,
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 10
  },
  exportingNote: {
    fontSize: 12,
    color: "#4A5568",
    textAlign: "right",
    marginTop: 8
  }
});
